

/* eslint-disable */
import React, { useState, useRef, useEffect, useId } from 'react';

function catmullRom(pts) {
  if (pts.length < 2) return '';
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(i - 1, 0)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(i + 2, pts.length - 1)];
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2[0]},${p2[1]}`;
  }
  return d;
}

//  Tooltip 

function Tooltip({ visible, style, children }) {
  return (
    <div
      className={`absolute z-50 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-lg text-xs pointer-events-none whitespace-nowrap transition-opacity duration-150 ${visible ? 'opacity-100' : 'opacity-0'}`}
      style={style}
    >
      {children}
    </div>
  );
}

//  Line Chart 

export function LineChart({ dates, series1, series2 }) {
  const W = 900, H = 180, PL = 32, PR = 12, PT = 8, PB = 26;
  const chartW = W - PL - PR;
  const chartH = H - PT - PB;
  const maxVal = Math.max(...series1, ...series2, 8);
  const n = dates.length;
  const uid = Math.random().toString(36).slice(2, 8);

  const xPos = (i) => PL + (i / (n - 1)) * chartW;
  const yPos = (v) => PT + chartH - (v / maxVal) * chartH;

  const pts1 = series1.map((v, i) => [xPos(i), yPos(v)]);
  const pts2 = series2.map((v, i) => [xPos(i), yPos(v)]);
  const line1 = catmullRom(pts1);
  const line2 = catmullRom(pts2);
  const area1 = `${line1} L${xPos(n - 1)},${PT + chartH} L${xPos(0)},${PT + chartH}Z`;
  const area2 = `${line2} L${xPos(n - 1)},${PT + chartH} L${xPos(0)},${PT + chartH}Z`;

  const yTicks = [0, 2, 4, 6, 8];
  // const visibleIdxs = dates.map((_, i) => i).filter((i) => i % 4 === 0 || i === n - 1);
    const MIN_LABEL_GAP =2; 
const visibleIdxs = (() => {
  const base = dates.map((_, i) => i).filter((i) => i % 4 === 0);
  const last = n - 1;
  const filtered = base.filter((i) => last - i >= MIN_LABEL_GAP || i === last);
  if (filtered[filtered.length - 1] !== last) filtered.push(last);
  return filtered;
})();

  const [tooltip, setTooltip] = useState(null);
  const [clipW, setClipW] = useState(0);
  const svgRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);

  // animation
  useEffect(() => {
    const el1 = line1Ref.current;
    const el2 = line2Ref.current;
    if (!el1 || !el2) return;

    const len1 = typeof el1.getTotalLength === 'function' ? el1.getTotalLength() : 1400;
    const len2 = typeof el2.getTotalLength === 'function' ? el2.getTotalLength() : 1400;

    el1.style.strokeDasharray = `${len1}`;
    el1.style.strokeDashoffset = `${len1}`;
    el2.style.strokeDasharray = `${len2}`;
    el2.style.strokeDashoffset = `${len2}`;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el1.style.transition = 'stroke-dashoffset 1.4s cubic-bezier(0.4,0,0.2,1) 0s';
        el1.style.strokeDashoffset = '0';
        el2.style.transition = 'stroke-dashoffset 1.4s cubic-bezier(0.4,0,0.2,1) 0.15s';
        el2.style.strokeDashoffset = '0';
        setClipW(chartW);
      });
    });
  }, []);

  const handleMouseMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const scaleX = W / rect.width;
    const mx = (e.clientX - rect.left) * scaleX;
    const rawIdx = ((mx - PL) / chartW) * (n - 1);
    const idx = Math.max(0, Math.min(n - 1, Math.round(rawIdx)));
    const pxX = (xPos(idx) / W) * rect.width;
    const pxYMin = (Math.min(yPos(series1[idx]), yPos(series2[idx])) / H) * rect.height;
    let left = pxX + 12;
    if (left + 165 > rect.width) left = pxX - 170;
    setTooltip({ idx, left, top: pxYMin - 10, svgX: xPos(idx) });
  };

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full overflow-visible block cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        <defs>
          <linearGradient id={`lg1${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2563eb" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0.01" />
          </linearGradient>
          <linearGradient id={`lg2${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#16a34a" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#16a34a" stopOpacity="0.01" />
          </linearGradient>
          <clipPath id={`c1${uid}`}>
            <rect
              x={PL} y={PT - 4}
              width={clipW}
              height={chartH + 8}
              style={{ transition: 'width 1.4s cubic-bezier(0.4,0,0.2,1) 0s' }}
            />
          </clipPath>
          <clipPath id={`c2${uid}`}>
            <rect
              x={PL} y={PT - 4}
              width={clipW}
              height={chartH + 8}
              style={{ transition: 'width 1.4s cubic-bezier(0.4,0,0.2,1) 0.15s' }}
            />
          </clipPath>
        </defs>

        {/* Grid lines */}
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={PL} y1={yPos(v)} x2={W - PR} y2={yPos(v)} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 4" />
            <text x={PL - 4} y={yPos(v) + 3} textAnchor="end" fontSize="8" fill="#9ca3af">{v}</text>
          </g>
        ))}
        {visibleIdxs.map((i) => (
          <line key={`vg${i}`} x1={xPos(i)} y1={PT} x2={xPos(i)} y2={PT + chartH} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 4" />
        ))}
        {visibleIdxs.map((i) => (
          <text key={i} x={xPos(i)} y={H - 3} textAnchor="middle" fontSize="8" fill="#9ca3af">{dates[i]}</text>
        ))}

        {/* Area fills (clipped to animate) */}
        <g clipPath={`url(#c1${uid})`}>
          <path d={area1} fill={`url(#lg1${uid})`} />
        </g>
        <g clipPath={`url(#c2${uid})`}>
          <path d={area2} fill={`url(#lg2${uid})`} />
        </g>

        {/* Lines */}
        <path ref={line1Ref} d={line1} fill="none" stroke="#2563eb" strokeWidth="1.8" />
        <path ref={line2Ref} d={line2} fill="none" stroke="#16a34a" strokeWidth="1.8" />

        {/* Hover crosshair */}
        {tooltip && (
          <g>
            <line x1={tooltip.svgX} y1={PT} x2={tooltip.svgX} y2={PT + chartH} stroke="#d1d5db" strokeWidth="1" strokeDasharray="3 3" />
            <circle cx={tooltip.svgX} cy={yPos(series1[tooltip.idx])} r="3.5" fill="#fff" stroke="#2563eb" strokeWidth="2" />
            <circle cx={tooltip.svgX} cy={yPos(series2[tooltip.idx])} r="3.5" fill="#fff" stroke="#16a34a" strokeWidth="2" />
          </g>
        )}
      </svg>

      {tooltip && (
        <Tooltip visible style={{ left: tooltip.left, top: tooltip.top }}>
          <div className="font-semibold text-gray-800 mb-1">{dates[tooltip.idx]}</div>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="font-medium text-blue-600">Active Users (DAU):</span>
            <span className="text-gray-600">{series1[tooltip.idx]}</span>
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="font-medium text-green-600">New Sign-ups:</span>
            <span className="text-gray-600">{series2[tooltip.idx]}</span>
          </div>
        </Tooltip>
      )}

      {/* Legend — line + diamond marker like reference */}
      <div className="flex justify-center gap-6 mt-3">
        {[
          { color: '#2563eb', label: 'Active Users (DAU)' },
          { color: '#16a34a', label: 'New Sign-ups' },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-2 text-xs text-gray-600">
            <svg width="30" height="10" viewBox="0 0 30 10">
              <line x1="0" y1="5" x2="30" y2="5" stroke={color} strokeWidth="1.8" />
              <polygon points="15,1 19,5 15,9 11,5" fill={color} />
            </svg>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

//  Donut Chart

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function buildArcPath(cx, cy, r, strokeW, startDeg, endDeg, expanded) {
  const sw = strokeW + (expanded ? 5 : 0);
  const iR = r - sw / 2;
  const oR = r + sw / 2;
  const sweep = Math.min(endDeg - startDeg, 359.99);
  const s1 = polarToCartesian(cx, cy, oR, startDeg);
  const e1 = polarToCartesian(cx, cy, oR, startDeg + sweep);
  const s2 = polarToCartesian(cx, cy, iR, startDeg + sweep);
  const e2 = polarToCartesian(cx, cy, iR, startDeg);
  const lg = sweep > 180 ? 1 : 0;
  return `M${s1.x.toFixed(3)} ${s1.y.toFixed(3)} A${oR} ${oR} 0 ${lg} 1 ${e1.x.toFixed(3)} ${e1.y.toFixed(3)} L${s2.x.toFixed(3)} ${s2.y.toFixed(3)} A${iR} ${iR} 0 ${lg} 0 ${e2.x.toFixed(3)} ${e2.y.toFixed(3)} Z`;
}

export function DonutChart({ plans }) {
  const [hovered, setHovered] = useState(null);
  const [ttPos, setTtPos] = useState({ x: 0, y: 0 });
  const [animPct, setAnimPct] = useState(0);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);

  const total = plans.reduce((s, p) => s + p.subscribers, 0);
  const CX = 80, CY = 80, R = 52, SW = 20;
  const GAP = 1.8;

  useEffect(() => {
    const duration = 950;
    let start = null;
    const tick = (ts) => {
      if (!start) start = ts;
      const t = Math.min((ts - start) / duration, 1);
      setAnimPct(1 - Math.pow(1 - t, 3)); // ease-out cubic
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  let cursor = 0;
  const segments = plans.map((p) => {
    const pct = total > 0 ? p.subscribers / total : 0;
    const deg = pct * 360;
    const seg = { ...p, pct, startFull: cursor, deg };
    cursor += deg;
    return seg;
  });

  const onEnter = (e, i) => {
    setHovered(i);
    if (wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setTtPos({ x: e.clientX - r.left + 10, y: e.clientY - r.top - 10 });
    }
  };
  const onMove = (e) => {
    if (wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setTtPos({ x: e.clientX - r.left + 10, y: e.clientY - r.top - 10 });
    }
  };

  return (
    <div className="flex flex-col items-center relative" ref={wrapRef}>
      <svg viewBox="0 0 160 160" width="160" height="160">
        {/* Track */}
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="#f3f4f6" strokeWidth={SW} />

        {segments.map((s, i) => {
          if (s.deg < 0.5) return null;
          const animatedStart = s.startFull * animPct;
          const animatedDeg   = s.deg * animPct;
          const hasGap = animatedDeg > GAP * 2;
          const gapAdj = hasGap ? GAP / 2 : 0;
          const startDeg = animatedStart + gapAdj;
          const endDeg   = animatedStart + animatedDeg - gapAdj;
          if (endDeg <= startDeg + 0.01) return null;
          const d = buildArcPath(CX, CY, R, SW, startDeg, endDeg, hovered === i);
          return (
            <path
              key={i}
              d={d}
              fill={s.color}
              className="cursor-pointer"
              style={{ transition: 'filter 0.15s' }}
              filter={hovered === i ? 'brightness(1.1)' : undefined}
              onMouseEnter={(e) => onEnter(e, i)}
              onMouseMove={onMove}
              onMouseLeave={() => setHovered(null)}
            />
          );
        })}
      </svg>

      {hovered !== null && (
        <Tooltip visible style={{ left: ttPos.x, top: ttPos.y, minWidth: 130 }}>
          <div className="font-semibold text-gray-800 mb-0.5">{plans[hovered].name}</div>
          <div className="text-gray-600">{plans[hovered].subscribers} subscribers</div>
          <div className="text-gray-400">{((plans[hovered].subscribers / total) * 100).toFixed(1)}%</div>
        </Tooltip>
      )}

      <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-2 justify-center max-w-xs">
        {plans.map((p, i) => (
          <div key={i} className="flex items-center gap-1 text-xs text-gray-600">
            <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: p.color }} />
            <span>{p.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

//  MRR Bar Chart 

export function MRRBarChart({ plans, currencySymbol = '$', axisMax, axisTicks }) {
  const [hovered, setHovered] = useState(null);
  const [ttPos, setTtPos] = useState({ x: 0, y: 0 });
  const [mounted, setMounted] = useState(false);
  const wrapRef = useRef(null);
  const maxMRR = axisMax || Math.max(...plans.map((p) => p.mrrVal), 1);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  const onEnter = (e, i) => {
    setHovered(i);
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      setTtPos({ x: e.clientX - rect.left + 10, y: e.clientY - rect.top - 10 });
    }
  };
  const onMove = (e) => {
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      setTtPos({ x: e.clientX - rect.left + 10, y: e.clientY - rect.top - 10 });
    }
  };

  const ticks = axisTicks || [0, maxMRR * 0.25, maxMRR * 0.5, maxMRR * 0.75, maxMRR];
  const axisLabels = ticks.map((v) => {
    if (v >= 1000) return `${currencySymbol}${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K`;
    return `${currencySymbol}${Math.round(v)}`;
  });

  return (
    <div ref={wrapRef} className="relative w-full">
      <div className="pr-2">
        {plans.map((p, i) => {
          const targetW = (p.mrrVal / maxMRR) * 100;
          return (
            <div
              key={i}
              className="flex items-center mb-2 gap-2 cursor-pointer"
              onMouseEnter={(e) => onEnter(e, i)}
              onMouseMove={onMove}
              onMouseLeave={() => setHovered(null)}
            >
              <div
                className="text-xs text-gray-600 text-right flex-shrink-0 overflow-hidden text-ellipsis whitespace-nowrap"
                style={{ width: 100 }}
                title={p.name}
              >
                {p.name}
              </div>
              <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded"
                  style={{
                    width: mounted ? `${targetW}%` : '0%',
                    opacity: hovered !== null && hovered !== i ? 0.4 : 1,
                    transition: `width 0.7s cubic-bezier(0.4,0,0.2,1) ${i * 90}ms, opacity 0.15s`,
                  }}
                />
              </div>
            </div>
          );
        })}

        {/* Axis */}
        <div className="flex justify-between mt-1" style={{ paddingLeft: 108 }}>
          {axisLabels.map((l) => (
            <span key={l} className="text-xs text-gray-400">{l}</span>
          ))}
        </div>
      </div>

      {hovered !== null && (
        <Tooltip visible style={{ left: ttPos.x, top: ttPos.y, minWidth: 120 }}>
          <div className="font-semibold text-gray-800 mb-0.5">{plans[hovered].name}</div>
          <div className="flex items-center gap-1">
            <span className="font-medium text-blue-600">MRR:</span>
            <span className="text-gray-600">{plans[hovered].mrr}</span>
          </div>
        </Tooltip>
      )}
    </div>
  );
}

export function AssessmentLineChart({ dates, attempts, uniqueUsers }) {
  const W = 900, H = 180, PL = 32, PR = 12, PT = 8, PB = 26;
  const chartW = W - PL - PR;
  const chartH = H - PT - PB;
  const maxVal = Math.max(...attempts, ...uniqueUsers, 4);
  const n = dates.length;
  const uid = Math.random().toString(36).slice(2, 8);

  const xPos = (i) => PL + (i / (n - 1)) * chartW;
  const yPos = (v) => PT + chartH - (v / maxVal) * chartH;

  const pts1 = attempts.map((v, i) => [xPos(i), yPos(v)]);
  const pts2 = uniqueUsers.map((v, i) => [xPos(i), yPos(v)]);
  const line1 = catmullRom(pts1);
  const line2 = catmullRom(pts2);
  const area1 = `${line1} L${xPos(n - 1)},${PT + chartH} L${xPos(0)},${PT + chartH}Z`;
  const area2 = `${line2} L${xPos(n - 1)},${PT + chartH} L${xPos(0)},${PT + chartH}Z`;

  const yTicks = [0, Math.round(maxVal * 0.25), Math.round(maxVal * 0.5), Math.round(maxVal * 0.75), maxVal];
  const MIN_LABEL_GAP = 2; 
const visibleIdxs = (() => {
  const base = dates.map((_, i) => i).filter((i) => i % 4 === 0);
  const last = n - 1;
  const filtered = base.filter((i) => last - i >= MIN_LABEL_GAP || i === last);
  if (filtered[filtered.length - 1] !== last) filtered.push(last);
  return filtered;
})();

  const [tooltip, setTooltip] = useState(null);
  const [clipW, setClipW] = useState(0);
  const svgRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);

  useEffect(() => {
    const el1 = line1Ref.current;
    const el2 = line2Ref.current;
    if (!el1 || !el2) return;
    const len1 = typeof el1.getTotalLength === 'function' ? el1.getTotalLength() : 1400;
    const len2 = typeof el2.getTotalLength === 'function' ? el2.getTotalLength() : 1400;
    el1.style.strokeDasharray = `${len1}`;
    el1.style.strokeDashoffset = `${len1}`;
    el2.style.strokeDasharray = `${len2}`;
    el2.style.strokeDashoffset = `${len2}`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el1.style.transition = 'stroke-dashoffset 1.4s cubic-bezier(0.4,0,0.2,1) 0s';
        el1.style.strokeDashoffset = '0';
        el2.style.transition = 'stroke-dashoffset 1.4s cubic-bezier(0.4,0,0.2,1) 0.15s';
        el2.style.strokeDashoffset = '0';
        setClipW(chartW);
      });
    });
  }, []);

  const handleMouseMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const scaleX = W / rect.width;
    const mx = (e.clientX - rect.left) * scaleX;
    const rawIdx = ((mx - PL) / chartW) * (n - 1);
    const idx = Math.max(0, Math.min(n - 1, Math.round(rawIdx)));
    const pxX = (xPos(idx) / W) * rect.width;
    const pxYMin = (Math.min(yPos(attempts[idx]), yPos(uniqueUsers[idx])) / H) * rect.height;
    let left = pxX + 12;
    if (left + 165 > rect.width) left = pxX - 170;
    setTooltip({ idx, left, top: pxYMin - 10, svgX: xPos(idx) });
  };

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full overflow-visible block cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        <defs>
          <linearGradient id={`alg1${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.01" />
          </linearGradient>
          <linearGradient id={`alg2${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.01" />
          </linearGradient>
          <clipPath id={`ac1${uid}`}>
            <rect x={PL} y={PT - 4} width={clipW} height={chartH + 8}
              style={{ transition: 'width 1.4s cubic-bezier(0.4,0,0.2,1) 0s' }} />
          </clipPath>
          <clipPath id={`ac2${uid}`}>
            <rect x={PL} y={PT - 4} width={clipW} height={chartH + 8}
              style={{ transition: 'width 1.4s cubic-bezier(0.4,0,0.2,1) 0.15s' }} />
          </clipPath>
        </defs>

        {yTicks.map((v) => (
          <g key={v}>
            <line x1={PL} y1={yPos(v)} x2={W - PR} y2={yPos(v)} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 4" />
            <text x={PL - 4} y={yPos(v) + 3} textAnchor="end" fontSize="8" fill="#9ca3af">{v}</text>
          </g>
        ))}
        {visibleIdxs.map((i) => (
          <line key={`vg${i}`} x1={xPos(i)} y1={PT} x2={xPos(i)} y2={PT + chartH} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 4" />
        ))}
        {visibleIdxs.map((i) => (
          <text key={i} x={xPos(i)} y={H - 3} textAnchor="middle" fontSize="8" fill="#9ca3af">{dates[i]}</text>
        ))}

        <g clipPath={`url(#ac1${uid})`}><path d={area1} fill={`url(#alg1${uid})`} /></g>
        <g clipPath={`url(#ac2${uid})`}><path d={area2} fill={`url(#alg2${uid})`} /></g>

        <path ref={line1Ref} d={line1} fill="none" stroke="#4f46e5" strokeWidth="1.8" />
        <path ref={line2Ref} d={line2} fill="none" stroke="#7c3aed" strokeWidth="1.8" />

        {tooltip && (
          <g>
            <line x1={tooltip.svgX} y1={PT} x2={tooltip.svgX} y2={PT + chartH} stroke="#d1d5db" strokeWidth="1" strokeDasharray="3 3" />
            <circle cx={tooltip.svgX} cy={yPos(attempts[tooltip.idx])} r="3.5" fill="#fff" stroke="#4f46e5" strokeWidth="2" />
            <circle cx={tooltip.svgX} cy={yPos(uniqueUsers[tooltip.idx])} r="3.5" fill="#fff" stroke="#7c3aed" strokeWidth="2" />
          </g>
        )}
      </svg>

      {tooltip && (
        <Tooltip visible style={{ left: tooltip.left, top: tooltip.top }}>
          <div className="font-semibold text-gray-800 mb-1">{dates[tooltip.idx]}</div>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="font-medium" style={{ color: '#4f46e5' }}>Attempts / Day:</span>
            <span className="text-gray-600">{attempts[tooltip.idx]}</span>
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="font-medium" style={{ color: '#7c3aed' }}>Unique Users / Day:</span>
            <span className="text-gray-600">{uniqueUsers[tooltip.idx]}</span>
          </div>
        </Tooltip>
      )}

      <div className="flex justify-center gap-6 mt-3">
        {[
          { color: '#4f46e5', label: 'Attempts / Day' },
          { color: '#7c3aed', label: 'Unique Users / Day' },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-2 text-xs text-gray-600">
            <svg width="30" height="10" viewBox="0 0 30 10">
              <line x1="0" y1="5" x2="30" y2="5" stroke={color} strokeWidth="1.8" />
              <polygon points="15,1 19,5 15,9 11,5" fill={color} />
            </svg>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default { LineChart, DonutChart, MRRBarChart, AssessmentLineChart };